import { type ReactNode } from 'react';

import { Box, Button, Stack } from '@mui/material';

import { SystemChromeContext } from '@/designs-dev/device/SystemChromeContext';
import { APP_HEADER_H } from '@/karehero-app/shell/layout';

// The member app at a desktop width: a bar that belongs to the window, and one
// column down the middle that holds a reading width.
//
// A column, not a stretched phone. The chat is a conversation, and a
// conversation read across 1440px is a worse conversation: the bubbles lose
// their shape, the composer strands itself a metre from the last thing said, and
// the eye has to travel to follow a reply. So the column holds a reading width
// and the page gives it the middle.
//
// A phone carries the logo and the way out in the app's own chrome, and a screen
// that fills a monitor has none, so the bar puts them back. Full width rather
// than capped to the column: it belongs to the window, not to the conversation.
//
// The chat on a desktop set this pattern, so every screen shown at a desktop
// width goes through here rather than restating it. It also switches the phone's
// system chrome off for whatever it holds: a clock and a battery drawn inside a
// browser window are a phone's furniture in a place that has its own.
export const DESKTOP_COLUMN = 720;

const SIGN_OUT = 'Sign out';

const DesktopShell = ({ children }: { children?: ReactNode }) => (
  <SystemChromeContext.Provider value={false}>
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          minHeight: APP_HEADER_H,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'custom.neutral.200',
        }}
        direction='row'
      >
        <Box
          sx={{ width: 30, height: 'auto' }}
          component='img'
          src='/vector/karehero-logo.png'
          alt='KareHero'
        />
        <Button variant='text' size='small'>
          {SIGN_OUT}
        </Button>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: DESKTOP_COLUMN,
            maxWidth: '100%',
            height: '100%',
            borderLeft: 1,
            borderRight: 1,
            borderColor: 'custom.neutral.200',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  </SystemChromeContext.Provider>
);

DesktopShell.displayName = 'DesktopShell';
export default DesktopShell;
